import type { ElementType } from 'react';
import MUIButton, {
  ButtonProps as MUIButtonProps,
  ButtonTypeMap as MUIButtonTypeMap,
} from '@mui/material/Button';
import { CSSObject, styled, alpha } from '../styles';
import Loader from '../Loader/Loader';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'destructive';

const MUI_VARIANT: { [key in Variant]: MUIButtonProps['variant'] } = {
  primary: 'contained',
  secondary: 'outlined',
  tertiary: 'text',
  destructive: 'text',
};

const ButtonStyled = styled(MUIButton, {
  shouldForwardProp: (prop) =>
    !['variantName', 'isOnContrasted', 'isLoading'].includes(prop as string),
})<{ variantName: Variant; isOnContrasted: boolean; isLoading?: boolean }>(
  ({ theme: { palette, spacing, shadows, radius }, variantName, isOnContrasted, isLoading }) => {
    const VARIANTS: { [key: string]: { default: CSSObject; onContrasted?: CSSObject } } = {
      primary: {
        default: {
          '&, &.Mui-disabled, &:hover': {
            backgroundColor: palette.interaction.enabled,
            color: palette.interaction.onEnabled,
          },
          '&:hover': {
            boxShadow: shadows.containedButtonRaised,
          },
        },
        onContrasted: {
          '&, &.Mui-disabled, &:hover': {
            backgroundColor: palette.interaction.onContrasted.enabled,
            color: palette.interaction.onContrasted.onEnabled,
          },
          '&:hover': {
            boxShadow: shadows.containedButtonRaised,
          },
        },
      },
      secondary: {
        default: {
          '&, &.Mui-disabled': {
            color: palette.interaction.enabled,
            padding: `calc(${spacing(1)} - 1px) calc(${spacing(2)} - 1px)`,
            border: `1px solid ${palette.interaction.enabled}`,
          },
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.hover, 0.16),
          },
        },
        onContrasted: {
          '&, &.Mui-disabled': {
            padding: `calc(${spacing(1)} - 1px) calc(${spacing(2)} - 1px)`,
            color: palette.interaction.onContrasted.enabled,
            border: `1px solid ${palette.interaction.onContrasted.enabled}`,
          },
          '&, &:hover': {
            color: palette.interaction.onContrasted.enabled,
            borderColor: palette.interaction.onContrasted.enabled,
          },
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.onContrasted.enabled, 0.16),
          },
        },
      },
      tertiary: {
        default: {
          '&, &.Mui-disabled': {
            padding: spacing(1),
          },
          '&, &.Mui-disabled, &:hover': {
            color: palette.interaction.enabled,
          },
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.hover, 0.16),
          },
          ...(isLoading && {
            '&': { backgroundColor: alpha(palette.interaction.hover, 0.16) },
          }),
        },
        onContrasted: {
          '&, &.Mui-disabled': {
            padding: spacing(1),
          },
          '&, &.Mui-disabled, &:hover': {
            color: palette.interaction.onContrasted.enabled,
          },
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.onContrasted.enabled, 0.16),
          },
          ...(isLoading && {
            '&': {
              backgroundColor: alpha(palette.interaction.onContrasted.enabled, 0.16),
            },
          }),
        },
      },
      destructive: {
        default: {
          '&, &.Mui-disabled': {
            padding: spacing(1),
            color: palette.interaction.danger,
          },
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.danger, 0.16),
          },
          ...(isLoading && {
            '&': { backgroundColor: alpha(palette.interaction.danger, 0.16) },
          }),
        },
      },
    };

    return {
      padding: spacing(1, 2),
      borderRadius: radius.button,
      '&:disabled': {
        opacity: isOnContrasted ? 0.64 : 0.38,
      },
      ...(VARIANTS[variantName][isOnContrasted ? 'onContrasted' : 'default'] ||
        VARIANTS[variantName].default),
      '&&:hover': {
        textDecoration: 'none',
      },
      ...(isLoading
        ? {
            pointerEvents: 'none',
            opacity: isOnContrasted ? 0.64 : 0.38,
          }
        : {}),
    };
  }
);

const Content = styled('span', {
  shouldForwardProp: (prop) => !['isLoading'].includes(prop as string),
})<{ isLoading?: boolean }>(({ isLoading }) => ({
  ...(isLoading ? { color: 'transparent' } : {}),
}));

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
    destructive: true;
  }
}

export type Props<D extends ElementType = MUIButtonTypeMap['defaultComponent']> = MUIButtonProps<
  D,
  { component?: D }
> & {
  isOnContrasted?: boolean;
  variant?: Variant;
  Component?: never;
  [dataAttribute: `data-${string}`]: any;
  isLoading?: boolean;
  loadingAriaLabel?: string;
  component?: ElementType | 'a';
};

const Button = ({
  variant = 'primary',
  isOnContrasted = false,
  children,
  sx = {},
  isLoading,
  loadingAriaLabel,
  startIcon,
  ref,
  ...otherProps
}: Props) => {
  const variantStyle = MUI_VARIANT[variant];
  return (
    <ButtonStyled
      variant={variantStyle}
      variantName={variant}
      isOnContrasted={isOnContrasted}
      isLoading={isLoading}
      ref={ref}
      sx={{
        ...(!children && {
          minWidth: 0,
          '.MuiButton-endIcon, .MuiButton-startIcon': {
            margin: 0,
          },
        }),
        ...sx,
      }}
      {...otherProps}
      {...(isLoading ? { 'aria-disabled': 'true', onClick: () => {} } : {})}
      startIcon={isLoading ? undefined : startIcon}
    >
      {isLoading && (
        <Loader
          size={24}
          sx={{ position: 'absolute', color: 'inherit' }}
          aria-label={loadingAriaLabel || ''}
        />
      )}
      <Content isLoading={isLoading}>{children}</Content>
    </ButtonStyled>
  );
};

export default Button;
